import {
  KitchenBoardQueryType,
  KitchenBoardType,
  KitchenColumnType,
  KitchenFinishRequestType,
  KitchenItemType,
  KitchenManualInputRequestType,
  KitchenOrderCardType,
  KitchenOrderDetailType,
  KitchenPrintResultType,
  KitchenResetRequestType,
  KitchenRejectRequestType,
  KitchenScanRequestType,
  KitchenTransitionRequestType,
} from 'features/Order/@types/KitchenMonitoring'
import { canTransitionKitchenOrder } from 'utils/helpers/kitchenTransition'

const STATUSES: KitchenColumnType['status'][] = ['INCOMING', 'PROCESSING', 'READY_TO_CHECK']
const stores = new Map<string, KitchenOrderDetailType[]>()

const createItems = (): KitchenItemType[] => [
  {
    id: 'food-1',
    sku: '123456789',
    name: 'Nasi Goreng Kampung Ayam Suwir dan Telur 400gram',
    station: 'FOOD',
    quantity: 3,
    processedQuantity: 0,
    checkedQuantity: 0,
    modifiers: [
      'Size: Jumbo',
      'Level: Pedas Dower',
      'Nasi: Porang',
      'Sambal: Kecombrang',
      'Telur: Omega DHA',
    ],
    addOns: ['Bawang Goreng', 'Half Telur Rebus Kecap', 'Cutlery Set'],
  },
  {
    id: 'food-2',
    sku: '123456790',
    name: 'Mie Goreng Tek-Tek Bakso Sapi dan Telur 380gram',
    station: 'FOOD',
    quantity: 1,
    processedQuantity: 0,
    checkedQuantity: 0,
    modifiers: ['Size: Jumbo', 'Level: Pedas Dower'],
  },
  {
    id: 'drink-1',
    sku: 'KITCHEN-DRINK-01',
    name: 'Kopi Susu',
    station: 'BEVERAGE',
    quantity: 1,
    processedQuantity: 0,
    checkedQuantity: 0,
    modifiers: ['Less ice'],
  },
]

const createOrders = (locationId: string): KitchenOrderDetailType[] =>
  ['INCOMING', 'INCOMING', 'PROCESSING', 'READY_TO_CHECK'].map((status, index) => {
    const items = createItems()
    const preparedItems = items.map((item) => {
      let processedQuantity = 0
      if (status === 'READY_TO_CHECK') processedQuantity = item.quantity
      else if (status === 'PROCESSING' && item.id === 'food-1') processedQuantity = 1
      const checkedQuantity = status === 'READY_TO_CHECK' && item.id === 'food-1' ? 1 : 0
      return { ...item, processedQuantity, checkedQuantity }
    })
    return {
      id: `kitchen-${locationId}-${index + 1}`,
      locationId,
      orderNumber: `#11159${index + 6}`,
      status: status as KitchenOrderDetailType['status'],
      dispatchAt: '21:15',
      createdAt: new Date(Date.now() - (index + 1) * 60_000).toISOString(),
      slaMinutes: 15,
      version: 1,
      items: preparedItems,
    }
  })

const getStore = (locationId: string) => {
  if (!stores.has(locationId)) stores.set(locationId, createOrders(locationId))
  return stores.get(locationId) || []
}

const findOrder = (locationId: string, orderId: string) => {
  const order = getStore(locationId).find(({ id }) => id === orderId)
  if (!order) throw new Error('Order Kitchen tidak ditemukan')
  return order
}

const copyOrder = (order: KitchenOrderDetailType): KitchenOrderDetailType => ({
  ...order,
  items: order.items.map((item) => ({
    ...item,
    modifiers: [...item.modifiers],
    addOns: item.addOns ? [...item.addOns] : undefined,
    manualInputs: item.manualInputs?.map((entry) => ({ ...entry })),
    rejection: item.rejection
      ? { ...item.rejection, options: [...item.rejection.options] }
      : undefined,
  })),
})

const toCard = (order: KitchenOrderDetailType): KitchenOrderCardType => ({
  id: order.id,
  locationId: order.locationId,
  orderNumber: order.orderNumber,
  status: order.status,
  dispatchAt: order.dispatchAt,
  createdAt: order.createdAt,
  slaMinutes: order.slaMinutes,
  version: order.version,
  foodQuantity: order.items
    .filter(({ station }) => station === 'FOOD')
    .reduce((total, item) => total + item.quantity, 0),
  beverageQuantity: order.items
    .filter(({ station }) => station === 'BEVERAGE')
    .reduce((total, item) => total + item.quantity, 0),
  foodProgressQuantity: order.items
    .filter(({ station }) => station === 'FOOD')
    .reduce(
      (total, item) =>
        total + (order.status === 'READY_TO_CHECK' ? item.checkedQuantity : item.processedQuantity),
      0,
    ),
  beverageProgressQuantity: order.items
    .filter(({ station }) => station === 'BEVERAGE')
    .reduce(
      (total, item) =>
        total + (order.status === 'READY_TO_CHECK' ? item.checkedQuantity : item.processedQuantity),
      0,
    ),
  processedQuantity: order.items.reduce((total, item) => total + item.processedQuantity, 0),
  checkedQuantity: order.items.reduce((total, item) => total + item.checkedQuantity, 0),
  totalQuantity: order.items.reduce((total, item) => total + item.quantity, 0),
})

// Dummy adapter for tests and explicitly development-only local previews.
// Endpoint paths, version rules, and production feature-flag key require the BE contract.
export const kitchenMonitoringService = {
  async getBoard({
    locationId,
    search = '',
    pageByStatus = {},
    pageSize = 20,
  }: KitchenBoardQueryType): Promise<KitchenBoardType> {
    const filtered = getStore(locationId).filter(({ orderNumber }) =>
      orderNumber.toLowerCase().includes(search.trim().toLowerCase()),
    )
    return {
      locationId,
      columns: STATUSES.map((status) => {
        const matches = filtered.filter((order) => order.status === status)
        const page = pageByStatus[status] || 1
        const end = page * pageSize
        return {
          status,
          total: matches.length,
          orders: matches.slice(0, end).map(toCard),
          page,
          pageSize,
          hasMore: end < matches.length,
        }
      }),
    }
  },

  async getDetail(locationId: string, orderId: string) {
    return copyOrder(findOrder(locationId, orderId))
  },

  async transition({
    locationId,
    orderId,
    targetStatus,
    expectedVersion,
  }: KitchenTransitionRequestType) {
    const order = findOrder(locationId, orderId)
    if (order.version !== expectedVersion) throw new Error('Order berubah. Muat ulang board.')
    if (!canTransitionKitchenOrder(order.status, targetStatus))
      throw new Error('Perpindahan status tidak diizinkan')
    order.status = targetStatus
    order.version += 1
    return copyOrder(order)
  },

  async scan({ locationId, orderId, code, action }: KitchenScanRequestType) {
    const order = findOrder(locationId, orderId)
    const itemId = code.trim().replace(/^kitchen-/, '')
    const item = order.items.find(({ id }) => id === itemId)
    if (!item) throw new Error('Kode item tidak ditemukan')
    if (item.availability === 'UNAVAILABLE') throw new Error('Item tidak tersedia untuk diproses')
    if (item.exception) throw new Error(`Item perlu perhatian: ${item.exception}`)
    if (action === 'PROCESS' && order.status !== 'PROCESSING')
      throw new Error('Order belum masuk tahap proses')
    if (action === 'CHECK' && order.status !== 'READY_TO_CHECK')
      throw new Error('Order belum siap dicek')
    if (action === 'CHECK' && item.inspectionStatus === 'MISMATCH')
      throw new Error('Item tidak sesuai. Periksa detail item.')
    if (action === 'CHECK' && item.inspectionStatus === 'EXCEPTION')
      throw new Error('Item perlu perhatian sebelum dicek')
    const field = action === 'PROCESS' ? 'processedQuantity' : 'checkedQuantity'
    const max = action === 'PROCESS' ? item.quantity : item.processedQuantity
    if (item[field] >= max) throw new Error('Jumlah item sudah lengkap')
    item[field] += 1
    order.version += 1
    return copyOrder(order)
  },

  async resetItem({ locationId, orderId, itemId }: KitchenResetRequestType) {
    const order = findOrder(locationId, orderId)
    const item = order.items.find(({ id }) => id === itemId)
    if (!item || order.status !== 'PROCESSING') throw new Error('Item tidak bisa direset')
    item.processedQuantity = 0
    order.version += 1
    return copyOrder(order)
  },

  async manualInput({
    locationId,
    orderId,
    itemId,
    sku,
    quantity,
    reason,
  }: KitchenManualInputRequestType) {
    const order = findOrder(locationId, orderId)
    const item = order.items.find(({ id }) => id === itemId)
    if (!item || order.status !== 'PROCESSING') throw new Error('Item tidak bisa diinput manual')
    if (item.id !== sku.trim()) throw new Error('Item ID tidak sesuai')
    if (!reason.trim()) throw new Error('Pilih alasan input manual')
    if (
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity > item.quantity - item.processedQuantity
    )
      throw new Error('Qty melebihi sisa item')
    if (item.availability === 'UNAVAILABLE' || item.exception)
      throw new Error('Item tidak tersedia untuk diproses')
    item.processedQuantity += quantity
    item.manualInputs = [...(item.manualInputs || []), { quantity, reason: reason.trim() }]
    order.version += 1
    return copyOrder(order)
  },

  async rejectItem({
    locationId,
    orderId,
    itemId,
    sku,
    quantity,
    reason,
    options,
  }: KitchenRejectRequestType) {
    const order = findOrder(locationId, orderId)
    const item = order.items.find(({ id }) => id === itemId)
    if (!item || order.status !== 'READY_TO_CHECK') throw new Error('Item tidak bisa direject')
    if (item.id !== sku.trim()) throw new Error('Item ID tidak sesuai')
    if (
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity > item.quantity - item.checkedQuantity
    )
      throw new Error('Qty reject melebihi sisa item')
    if (item.inspectionStatus) throw new Error('Item sudah ditandai bermasalah')
    let allowed: string[] = []
    if (reason === 'Salah Modifier') allowed = item.modifiers
    if (reason === 'Add-ons Kurang') allowed = item.addOns || []
    if (
      (reason === 'Salah Modifier' || reason === 'Add-ons Kurang') &&
      (!options.length || options.some((option) => !allowed.includes(option)))
    )
      throw new Error('Pilih opsi yang bermasalah')
    item.inspectionStatus = 'MISMATCH'
    item.rejection = { quantity, reason, options: [...options] }
    order.version += 1
    return copyOrder(order)
  },

  async finish({ locationId, orderId, action }: KitchenFinishRequestType) {
    const order = findOrder(locationId, orderId)
    const from = action === 'PROCESS' ? 'PROCESSING' : 'READY_TO_CHECK'
    const field = action === 'PROCESS' ? 'processedQuantity' : 'checkedQuantity'
    if (
      order.status !== from ||
      !order.items.length ||
      order.items.some(
        (item) =>
          item[field] < item.quantity ||
          (action === 'CHECK' && !!item.inspectionStatus) ||
          !!item.exception ||
          item.availability === 'UNAVAILABLE',
      )
    )
      throw new Error('Masih ada item yang belum lengkap')
    order.status = action === 'PROCESS' ? 'READY_TO_CHECK' : 'COMPLETED'
    order.version += 1
    return copyOrder(order)
  },

  async getPrinterStatus(locationId: string) {
    const available = locationId !== 'printer-offline'
    return {
      available,
      simulated: true,
      sections: ['MAKANAN', 'MINUMAN', 'CHECKER'].map((section_name) => ({
        section_name,
        status: available ? 'PRINTER_STATUS_ONLINE' : 'PRINTER_STATUS_OFFLINE',
      })),
    }
  },

  async partialFulfill(
    locationId: string,
    orderId: string,
    itemId: string,
    quantity: number,
    note: string,
  ) {
    const order = findOrder(locationId, orderId)
    const item = order.items.find(({ id }) => id === itemId)
    if (
      !item ||
      !Number.isInteger(quantity) ||
      quantity < 0 ||
      quantity >= item.quantity ||
      !note.trim()
    )
      throw new Error('Periksa Qty dan catatan partial fulfil')
    const remainingQty = item.quantity - quantity
    item.quantity = quantity
    item.processedQuantity = Math.min(item.processedQuantity, quantity)
    item.checkedQuantity = Math.min(item.checkedQuantity, quantity)
    order.version += 1
    return { fulfill_quantity: quantity, remaining_qty: remainingQty }
  },

  async markPrinted(_orderId: string, itemIds: string[]) {
    return { marked_count: itemIds.length }
  },

  async printLabel(locationId: string, orderId: string): Promise<KitchenPrintResultType> {
    findOrder(locationId, orderId)
    if (!(await this.getPrinterStatus(locationId)).available)
      throw new Error('Printer simulasi tidak tersedia')
    return { queued: true, message: 'Simulasi cetak label berhasil' }
  },
}
